import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { formatDate } from '@angular/common';
import {DragDropModule} from '../../../drag-drop/drag-drop.module';
import {VendorsService} from '../../../../core/services/vendors.service';
import {toSignal} from '@angular/core/rxjs-interop';
import {AdsService} from '../../../../core/services/cms/ads.service';
import {MAT_DATE_LOCALE, provideNativeDateAdapter} from '@angular/material/core';

@Component({
    selector: 'phx-ads-form-2026',
    imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    MatCheckboxModule,
    DragDropModule
],
    templateUrl: './ads-form-2026.component.html',
    styleUrl: './ads-form-2026.component.scss',
    providers: [
        provideNativeDateAdapter(),
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' }
    ],
})
export class AdsForm2026Component implements OnInit {
    private fb = inject(FormBuilder);
    private dialogRef = inject(MatDialogRef<AdsForm2026Component>);
    protected CDN = inject('CDN' as any) as string;
    private vendorsService = inject(VendorsService);
    private adsService = inject(AdsService);

    data = inject<{ ad?: any, mode: 'create' | 'edit' }>(MAT_DIALOG_DATA);

    adsForm!: FormGroup;

    imagePreview = signal<string>('');
    invalid = signal<string[]>([]);
    portals = signal<string[]>([]);
    formValid = signal<boolean>(false);
    readonly locations = this.adsService.locations;

    targets = signal([
        {value: '_blank', label: 'Neuer Tab '},
        {value: '_self', label: 'Gleicher Tab'}
    ]);

    vendors = toSignal(
        this.vendorsService.getAllVendorsObservable(),
        {initialValue: []}
    );

    title = computed(() =>
        this.data.mode === 'create' ? 'Neue Banner Ad' : 'Banner Ad bearbeiten'
    );

    submitButtonText = computed(() =>
        this.data.mode === 'create' ? 'Banner Ad speichern' : 'Banner Ad aktualisieren'
    );

    isFormValid = computed(() => {
        return this.formValid() && this.portals().length > 0;
    });

    imageUrl = computed(() => {
        const preview = this.imagePreview();
        if (!preview) {
            return '';
        }
        if (preview.startsWith('data:')) {
            return preview;
        }
        return `${this.CDN}/marketing-management/${preview}`;
    });

    private idfList: any[] = [];

    ngOnInit() {
        this.vendorsService.loadAllVendors();
        this.initForm();

        if (this.data.mode === 'edit' && this.data.ad) {
            this.patchFormValues();
        }

        this.formValid.set(this.adsForm.valid);

        this.adsForm.statusChanges.subscribe(() => {
            this.formValid.set(this.adsForm.valid);
        });
    }

    getFormValidationErrors() {
        const errors: any = {};
        Object.keys(this.adsForm.controls).forEach(key => {
            const control = this.adsForm.get(key);
            if (control && control.errors) {
                errors[key] = control.errors;
            }
        });
        return errors;
    }

    private initForm() {
        this.adsForm = this.fb.group({
            id: [null],
            title: ['', Validators.required],
            link: [''],
            location: ['', Validators.required],
            target: ['_blank'],
            vendor: [null, Validators.required],
            start: [null, Validators.required],
            end: [null, Validators.required],
            idfs: [[]],
            image: [null, Validators.required],
        });
    }

    private patchFormValues() {
        const ad = this.data.ad;

        this.adsForm.patchValue({
            id: ad.id,
            title: ad.title,
            link: ad.link,
            location: ad.location,
            target: ad.target || '_blank',
            vendor: ad.vendor?.id || null,
            start: ad.start_date ? new Date(ad.start_date) : null,
            end: ad.end_date ? new Date(ad.end_date) : null,
            idfs: ad.idfs || [],
            image: ad.image || ''
        });

        if (ad.image) {
            this.imagePreview.set(ad.image);
        }

        let selectedPortals: string[] = [];

        if (Array.isArray(ad.company)) {
            selectedPortals = ad.company.filter((c: string) =>
                c === 'HAGEDA' || c === 'PHOENIX'
            );
        } else if (typeof ad.company === 'string') {
            if (ad.company.indexOf('HAGEDA') > -1) {
                selectedPortals.push('HAGEDA');
            }
            if (ad.company.indexOf('PHOENIX') > -1) {
                selectedPortals.push('PHOENIX');
            }
        }

        this.portals.set(selectedPortals);
    }

    togglePortal(portal: string) {
        const current = this.portals();
        if (current.includes(portal)) {
            this.portals.set(current.filter(p => p !== portal));
        } else {
            this.portals.set([...current, portal]);
        }
    }

    isPortalSelected(portal: string): boolean {
        return this.portals().includes(portal);
    }

    onImageSelected(file: any) {

        if (file && file instanceof File) {
            this.adsForm.patchValue({image: file});

            this.adsForm.get('image')?.markAsTouched();
            this.adsForm.get('image')?.updateValueAndValidity();

            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.imagePreview.set(e.target.result);
            };
            reader.readAsDataURL(file);
        } else {
            console.error('📸 Not a valid File object');
        }
    }

    clearImageSelection() {
        this.imagePreview.set('');
        this.adsForm.patchValue({image: null});
        this.adsForm.get('image')?.markAsTouched();
        this.adsForm.get('image')?.updateValueAndValidity();
    }

    insertIdfs(idfs) {
        this.idfList = idfs;
        this.adsForm.patchValue({idfs: idfs});
    }

    insertIdfsAsList(event) {
        const list = event.target.value;
        const newIdfs = list.split(',')
            .map(item => item.trim())
            .filter(item => item !== '');
        this.insertIdfs(newIdfs.concat(this.idfList));
    }

    onCreateAd() {
        this.adsForm.markAllAsTouched();

        if (!this.isFormValid()) {
            console.log('Form is invalid:', this.getFormValidationErrors());
            return;
        }

        const formValue = this.adsForm.value;

        if (!formValue.image || !(formValue.image instanceof File)) {
            console.error('Image is required!');
            return;
        }

        const formattedData = {
            ...formValue,
            start: formValue.start ? formatDate(new Date(formValue.start.toString()), 'yyyy-MM-dd', 'de_DE') : null,
            end: formValue.end ? formatDate(new Date(formValue.end.toString()), 'yyyy-MM-dd', 'de_DE') : null,
            company: this.portals(),
            image: ''
        };

        this.dialogRef.close({
            data: formattedData,
            imageFile: formValue.image
        });
    }

    onEditAd() {
        this.adsForm.markAllAsTouched();

        if (!this.isFormValid()) {
            return;
        }

        const formValue = this.adsForm.value;

        const formattedData = {
            ...formValue,
            start: formValue.start ? formatDate(new Date(formValue.start.toString()), 'yyyy-MM-dd', 'de_DE') : null,
            end: formValue.end ? formatDate(new Date(formValue.end.toString()), 'yyyy-MM-dd', 'de_DE') : null,
            company: this.portals(),
        };

        this.dialogRef.close({
            data: formattedData,
            imageFile: formValue.image instanceof File ? formValue.image : null
        });
    }

    onCancel() {
        this.dialogRef.close();
    }

    onLinkBlur(): void {
        const control = this.adsForm.get('link');
        const value = control?.value?.trim();

        if (!value) {
            return;
        }

        if (!value.startsWith('http://') && !value.startsWith('https://')) {
            control?.setValue('https://' + value);
        }
    }
}
